import { useCallback, useEffect } from 'react';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useTraitsActions } from '@atlassian/jira-traits/src/TraitsStore.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { SITE_USER } from '@atlassian/jira-traits/src/constants.tsx';
import { useIsJiraNewUserTraitSelector } from './useIsJiraNewUserTraitSelector.tsx';

interface YellowBrickRoad {
	shouldDisplayMessage: boolean;
	// TODO will be implemented in https://hello.jira.atlassian.cloud/browse/PACK-278
	messageId?: string;
}

export const useYbrFeature = () => {
	const cloudId = useCloudId();
	const accountId = useAccountId();
	const { fetchTraits } = useTraitsActions();
	useEffect(() => {
		if (cloudId && accountId && fg('jira_ybr_precondition_check_rollout')) {
			fetchTraits([SITE_USER], { cloudId, accountId });
		}
	}, [cloudId, accountId, fetchTraits]);

	const [isJiraNewUser] = useIsJiraNewUserTraitSelector();

	const shouldShowYbrMessage = useCallback(
		({ shouldDisplayMessage }: YellowBrickRoad) => {
			// If the user is not a new user or the message is not enabled, we should return the original condition of the message
			if (!shouldDisplayMessage || !isJiraNewUser) {
				return shouldDisplayMessage;
			}

			expVal('jira_ybr_first_batch_aa', 'isYBREnabled', false);
			expVal('jira_ybr_first_batch_both_aa', 'isYBREnabled', false);

			// we should not show the message if the experiment is enabled
			return !expVal('jira_ybr_first_batch', 'isYBREnabled', false);
		},
		[isJiraNewUser],
	);

	const shouldShowYbrMessageForIssueView = useCallback(
		({ shouldDisplayMessage }: YellowBrickRoad) => {
			// Prevent SSR for showing the pinned-field first, and then hide it due to the experiment cohort
			if (__SERVER__ && fg('jira_ybr_precondition_check_rollout')) {
				return false;
			}

			// If the user is not a new user or the message is not enabled, we should return the original condition of the message
			if (!shouldDisplayMessage || !isJiraNewUser) {
				return shouldDisplayMessage;
			}

			expVal('jira_ybr_first_batch_issue_view_aa', 'isYBREnabled', false);
			expVal('jira_ybr_first_batch_both_aa', 'isYBREnabled', false);

			// we should not show the message if the experiment is enabled
			return !expVal('jira_ybr_first_batch', 'isYBREnabled', false);
		},
		[isJiraNewUser],
	);

	return {
		shouldShowYbrMessage,
		shouldShowYbrMessageForIssueView,
	};
};
